import { Box, Typography } from '@mui/material';
import { CircleCheck, CircleHelp, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';

import { Badge, Card, Icon, type BadgeTone } from '@/ui/kit';
import { fontStacks } from '@/ui/tokens';

import { stockSearchPath } from './routes';
import {
  checkOutcome,
  checkTitle,
  describeViolation,
  shortId,
  summarizeIntegrity,
  type CheckOutcome,
} from './stockPresentation';
import type { StockIntegrityCheck } from './stockTypes';

/**
 * Liste de controles d'integrite : ceux d'un lot, ou ceux de tout un perimetre.
 *
 * Trois etats, chacun avec son mot ET son pictogramme — jamais la couleur seule —,
 * et **aucun ne se confond avec un autre** :
 *
 * - **Conforme** : le controle a tout pu juger et n'a rien releve ;
 * - **Incoherence** : un constat, formule en phrase depuis le detail du serveur ;
 * - **Non évaluable** : le controle n'a pas pu juger (champ illisible, charge
 *   hors forme). Ce n'est **pas** « conforme » : un controle dont certains
 *   elements n'ont pas pu etre lus ne l'est jamais, meme sans constat.
 *
 * Le vocabulaire est celui du serveur : une « incohérence de données » decrit le
 * journal, pas la personne qui l'a ecrit. Le lot en cause d'un constat est un lien
 * vers sa trace, sauf s'il s'agit du lot ouvert.
 */

const OUTCOMES: Readonly<
  Record<CheckOutcome, { readonly label: string; readonly tone: BadgeTone; readonly icon: LucideIcon; readonly ink: string }>
> = {
  conforming: { label: 'Conforme', tone: 'success', icon: CircleCheck, ink: 'tokens.successText' },
  violation: { label: 'Incohérence', tone: 'warning', icon: TriangleAlert, ink: 'tokens.accentYellowStrong' },
  'not-evaluable': { label: 'Non évaluable', tone: 'neutral', icon: CircleHelp, ink: 'tokens.textMuted' },
};

const plural = (count: number, one: string, many: string): string => `${String(count)} ${count > 1 ? many : one}`;

const notJudged = (count: number): string =>
  `${plural(count, 'élément n’a', 'éléments n’ont')} pas pu être jugé${count > 1 ? 's' : ''}`;

const describeSummary = (checks: readonly StockIntegrityCheck[]): string => {
  const { inconsistencies, conforming, notEvaluable } = summarizeIntegrity(checks);

  return [
    inconsistencies === 0
      ? 'Aucune incohérence de données relevée'
      : plural(inconsistencies, 'incohérence de données relevée', 'incohérences de données relevées'),
    plural(conforming, 'contrôle conforme', 'contrôles conformes'),
    plural(notEvaluable, 'contrôle non évaluable', 'contrôles non évaluables'),
  ].join(' · ');
};

export interface IntegrityChecksProps {
  readonly title: string;
  readonly subtitle: ReactNode;
  readonly icon?: LucideIcon;
  /** Nom de la liste pour les lecteurs d'écran. */
  readonly listLabel: string;
  readonly checks: readonly StockIntegrityCheck[];
  /** Lot ouvert : nommé « ce lot » plutôt que lié. */
  readonly currentBatchId: string;
  /** Code de chaque lot connu ; un lot absent est désigné par le début de son identifiant. */
  readonly codes: ReadonlyMap<string, string | null>;
  /** Phrase sous le résumé, par exemple le nombre de lots contrôlés. */
  readonly scopeNote?: string;
}

export const IntegrityChecks = ({
  title,
  subtitle,
  icon,
  listLabel,
  checks,
  currentBatchId,
  codes,
  scopeNote,
}: IntegrityChecksProps): ReactElement => (
  <Card title={title} icon={icon} subtitle={subtitle}>
    <Typography role="status" sx={{ fontSize: 14, fontWeight: 600 }}>
      {describeSummary(checks)}
    </Typography>
    {scopeNote === undefined ? null : (
      <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>{scopeNote}</Typography>
    )}

    {checks.length === 0 ? (
      <Typography sx={{ fontSize: 14, color: 'tokens.textMuted' }}>Le serveur n’a rendu aucun contrôle.</Typography>
    ) : (
      <Box component="ul" aria-label={listLabel} sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1.5 }}>
        {checks.map((check) => {
          const outcome = checkOutcome(check);
          const { label, tone, icon: outcomeIcon, ink } = OUTCOMES[outcome];

          return (
            <Box component="li" key={check.code} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', py: 0.5 }}>
              <Box component="span" sx={{ display: 'inline-flex', color: ink, pt: '2px' }}>
                <Icon icon={outcomeIcon} size={18} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
                    {checkTitle(check.code)}
                  </Typography>
                  <Badge label={label} tone={tone} />
                </Box>

                {outcome === 'not-evaluable' ? (
                  <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
                    {check.notEvaluable > 0
                      ? `Rien d’incohérent dans ce qui a pu être lu, mais ${notJudged(check.notEvaluable)}.`
                      : 'Le serveur n’a pas rendu de résultat exploitable pour ce contrôle.'}
                  </Typography>
                ) : null}

                {check.violations.length === 0 ? null : (
                  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.75 }}>
                    {check.violations.map((violation, index) => (
                      // Pas d'identifiant propre a un constat : un lot peut en porter
                      // plusieurs pour un meme controle (une repartition par exemple).
                      <Box component="li" key={`${violation.batchId}-${String(index)}`}>
                        <Typography component="div" sx={{ fontSize: 13 }}>
                          {describeViolation(check.code, violation.detail)}
                        </Typography>
                        <Typography component="div" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
                          {violation.batchId === currentBatchId ? (
                            'Lot concerné : ce lot'
                          ) : (
                            <>
                              Lot concerné :{' '}
                              <Box
                                component={Link}
                                to={stockSearchPath(violation.batchId)}
                                sx={{ color: 'inherit', fontFamily: fontStacks.mono, textUnderlineOffset: 3, overflowWrap: 'anywhere' }}
                              >
                                {codes.get(violation.batchId) ?? `lot ${shortId(violation.batchId)}`}
                              </Box>
                            </>
                          )}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {outcome === 'violation' && check.notEvaluable > 0 ? (
                  <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>{`Et ${notJudged(check.notEvaluable)}.`}</Typography>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>
    )}
  </Card>
);
